// Single reader for the clinical-proband designation (TGC-1429 remediation).
//
// TGC-1429 made the proband movable (promote) and clearable (Remove Proband),
// which splits "who gets the arrow" into three states:
//
//   explicit — some member carries isProband === true (promoted or designated
//              at creation). Arrow on that member.
//   cleared  — no member is true and the structural ROOT row carries
//              probandCleared === true: a clinician explicitly removed the
//              proband. No arrow — "no active proband" is a real state.
//   legacy   — neither of the above: imported or pre-0015 data that never
//              carried a designation. Fall back to the structural root so old
//              pedigrees keep their arrow.
//
// WHY A DEDICATED TRUTHY SENTINEL, AND NOT isProband === false ON THE ROOT:
// that was the original encoding and it was unsound in both directions.
//
//   Destructible — cleanFormData (backend/util/util.js) copies a key only when
//   its value is truthy, and GET /pedigree/details runs the root document
//   through it. The clinician's false was written correctly to Mongo and then
//   deleted on the way out, so the client saw `undefined`, this selector fell
//   through to 'legacy', and the arrow snapped back to the structural root on
//   every reload. Promote appeared to work only because a member's surviving
//   `true` short-circuits before the root is ever inspected.
//
//   Forgeable — familySchema declares `isProband: { type: Boolean, default:
//   false }`, so any hydrate-and-save or upsert with setDefaultsOnInsert
//   materialises a false byte-identical to a deliberate Remove. Migration
//   0016's own header calls this ambiguity out.
//
// `probandCleared` has no schema default and is only ever written by the Remove
// path (and $unset by promote), so presence proves intent; being truthy, it
// cannot be dropped by a falsy filter. Do NOT re-admit `isProband === false` as
// a fallback here: every family currently holding one renders as 'legacy'
// today (the false never reached the client), so honouring it would make an
// unknown number of families silently lose their arrow on deploy.
//
// The two designation fields are deliberately decoupled — this selector reads
// only probandCleared for 'cleared' and only member-level `=== true` for
// 'explicit', so a drifted root isProband is inert. Do not re-couple them.
//
// Every consumer (Canvas arrow, ContextMenu Make/Remove Proband, ChartBox
// migrate guard) must go through this selector rather than re-interpreting
// flag presence.
export function getProbandState(members) {
  const list = Array.isArray(members) ? members : [];

  const explicit = list.find((m) => m?.isProband === true);
  if (explicit) {
    return { state: 'explicit', probandPid: explicit.pid ?? null };
  }

  // The structural root is the one row carrying proBandId. Defensive: with
  // zero (or anomalous duplicate) proBandId rows we degrade to "no arrow"
  // rather than guessing.
  const root = list.find((m) => m?.proBandId != null);
  if (!root) {
    return { state: 'legacy', probandPid: null };
  }

  if (root.probandCleared === true) {
    return { state: 'cleared', probandPid: null };
  }

  return { state: 'legacy', probandPid: root.pid ?? null };
}
